import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ArrowRight,
  Cog,
  ShoppingCart,
  Wrench,
  Package,
  Gauge,
  Cpu,
  Hammer,
  GraduationCap,
  Settings2,
  type LucideIcon,
} from 'lucide-react'
import { PageHeader } from '@/components/PageHeader'
import { LocalizedLink } from '@/components/LocalizedLink'
import { usePageSEO } from '@/hooks/usePageSEO'
import { cn } from '@/lib/utils'

type ServiceKey =
  | 'design'
  | 'sales'
  | 'assistance'
  | 'spareParts'
  | 'spindles'
  | 'retrofit'
  | 'mechanical'
  | 'training'
  | 'custom'

interface ServiceSpec {
  key: ServiceKey
  icon: LucideIcon
}

const SERVICES: ServiceSpec[] = [
  { key: 'design', icon: Cog },
  { key: 'sales', icon: ShoppingCart },
  { key: 'assistance', icon: Wrench },
  { key: 'spareParts', icon: Package },
  { key: 'spindles', icon: Gauge },
  { key: 'retrofit', icon: Cpu },
  { key: 'mechanical', icon: Hammer },
  { key: 'training', icon: GraduationCap },
  { key: 'custom', icon: Settings2 },
]

export default function ServicesPage() {
  const { t } = useTranslation()
  const location = useLocation()

  usePageSEO({
    title: t('meta.services.title'),
    description: t('meta.services.description'),
    ogTitle: t('meta.services.ogTitle'),
    ogDescription: t('meta.services.ogDescription'),
    ogImage: '/images/hero/services.jpg',
  })

  // Compute anchor ids for the current language from translation keys.
  // This is what keeps IT /#progettazione and EN /#design both aligned
  // with the Home page teasers.
  const services = useMemo(
    () =>
      SERVICES.map((s) => ({
        ...s,
        id: t(`services.sectionIds.${s.key}`),
        title: t(`services.items.${s.key}.title`),
        short: t(`services.items.${s.key}.short`),
        alt: t(`services.items.${s.key}.alt`),
        image: t(`services.items.${s.key}.image`),
        paragraphs: t(`services.items.${s.key}.paragraphs`, {
          returnObjects: true,
        }) as string[],
      })),
    [t]
  )

  // Scroll-spy: track which service section is currently in view.
  const [activeId, setActiveId] = useState<string>(services[0]?.id ?? '')
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({})

  useEffect(() => {
    const observers: IntersectionObserver[] = []
    const targets = services
      .map((s) => sectionRefs.current[s.id])
      .filter((el): el is HTMLElement => Boolean(el))

    if (targets.length === 0) return

    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the entry that is most visible (largest intersectionRatio)
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)
        if (visible.length > 0) {
          const id = visible[0].target.id
          if (id) setActiveId(id)
        }
      },
      {
        // Consider a section active when its upper third crosses the header
        rootMargin: '-30% 0px -55% 0px',
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    )

    for (const el of targets) observer.observe(el)
    observers.push(observer)

    return () => {
      for (const o of observers) o.disconnect()
    }
  }, [services])

  // On mount (or when hash changes) smooth-scroll to the requested anchor.
  // We defer the scroll by one frame so that the layout is measured and the
  // sticky header offset is correct.
  useEffect(() => {
    const hash = location.hash.replace('#', '')
    if (!hash) return
    const el = document.getElementById(hash)
    if (!el) return
    const raf = requestAnimationFrame(() => {
      const top =
        el.getBoundingClientRect().top +
        window.scrollY -
        96 /* account for sticky header / mobile chips */
      window.scrollTo({ top, behavior: 'smooth' })
      setActiveId(hash)
    })
    return () => cancelAnimationFrame(raf)
  }, [location.hash, services])

  const handleJumpClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
      e.preventDefault()
      const el = document.getElementById(id)
      if (!el) return
      const top =
        el.getBoundingClientRect().top + window.scrollY - 96
      window.scrollTo({ top, behavior: 'smooth' })
      // Keep URL in sync for shareability
      window.history.replaceState(null, '', `#${id}`)
      setActiveId(id)
    },
    []
  )

  return (
    <main id="main-content" className="bg-ink-100">
      {/* ============================================================
          1 · HERO — standardized blueprint PageHeader
          ============================================================ */}
      <PageHeader
        variant="dark"
        eyebrow={t('services.eyebrow')}
        title={t('services.title')}
        subtitle={t('services.headerSubtitle')}
      />

      {/* ============================================================
          2 · MOBILE JUMP-MENU — sticky horizontal scroll strip
          ============================================================ */}
      <nav
        aria-label={t('services.jumpMenuLabel')}
        className="lg:hidden sticky top-0 z-30 border-b border-ink-200 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/85"
      >
        <div className="relative">
          {/* Left / right fade hints */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 left-0 w-6 bg-gradient-to-r from-white to-transparent z-10"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-white to-transparent z-10"
          />

          <ul
            className="flex gap-2 overflow-x-auto px-4 py-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            role="list"
          >
            {services.map((s, i) => {
              const isActive = activeId === s.id
              return (
                <li key={s.key} className="shrink-0">
                  <a
                    href={`#${s.id}`}
                    onClick={(e) => handleJumpClick(e, s.id)}
                    className={cn(
                      'group inline-flex items-center gap-2 rounded-sm border px-3 py-2 text-[11px] font-bold uppercase tracking-[0.14em] transition-colors duration-200',
                      isActive
                        ? 'border-brand-500 bg-brand-500 text-white'
                        : 'border-ink-200 bg-white text-ink-700 hover:border-brand-500 hover:text-brand-700'
                    )}
                    aria-current={isActive ? 'true' : undefined}
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        'font-display tabular-nums text-[10px]',
                        isActive ? 'text-white/80' : 'text-brand-600'
                      )}
                    >
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span>{s.title}</span>
                  </a>
                </li>
              )
            })}
          </ul>
        </div>
      </nav>

      {/* ============================================================
          3 · INTRO SLAB — introduces the 9-service cycle
          ============================================================ */}
      <section
        aria-labelledby="services-intro-title"
        className="relative bg-white border-b border-ink-200"
      >
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-7">
              <p className="mb-4 inline-flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.28em] text-brand-600">
                <span aria-hidden="true" className="block h-px w-10 bg-brand-500" />
                {t('services.introEyebrow')}
              </p>
              <h2
                id="services-intro-title"
                className="fx-text-balance font-bold leading-[1.1] text-ink-900 text-3xl sm:text-4xl md:text-[2.5rem]"
              >
                {t('services.introTitle')}
              </h2>
              <p className="mt-6 max-w-2xl fx-text-pretty text-base md:text-lg leading-relaxed text-ink-600">
                {t('services.intro')}
              </p>
            </div>

            {/* Right-side quick stat: 9 services */}
            <div className="lg:col-span-5">
              <div className="relative border-l-[3px] border-brand-500 pl-6 md:pl-8">
                <span
                  aria-hidden="true"
                  className="absolute -left-[3px] top-0 block h-4 w-[3px] bg-ink-900"
                />
                <span
                  aria-hidden="true"
                  className="absolute -left-[3px] bottom-0 block h-4 w-[3px] bg-ink-900"
                />
                <dl className="space-y-6">
                  <div className="flex items-baseline gap-5">
                    <dt className="shrink-0 font-display text-4xl md:text-5xl font-bold leading-none tabular-nums text-ink-900">
                      09
                    </dt>
                    <dd className="text-xs md:text-sm uppercase tracking-[0.18em] text-ink-600 font-semibold">
                      {t('services.itemsTitle')}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          4 · MAIN — sticky sidebar (lg+) + stacked service sections
          ============================================================ */}
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          {/* ---------- Desktop jump menu (sticky sidebar) ---------- */}
          <aside
            aria-label={t('services.jumpMenuLabel')}
            className="hidden lg:block lg:col-span-3"
          >
            <div className="sticky top-28">
              <p className="mb-5 inline-flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.28em] text-brand-600">
                <span aria-hidden="true" className="block h-px w-8 bg-brand-500" />
                {t('services.jumpMenuLabel')}
              </p>
              <ol className="border-l border-ink-200" role="list">
                {services.map((s, i) => {
                  const isActive = activeId === s.id
                  return (
                    <li key={s.key} className="relative">
                      {/* Active indicator tick */}
                      <span
                        aria-hidden="true"
                        className={cn(
                          'absolute -left-px top-1/2 block h-[60%] w-[3px] -translate-y-1/2 transition-all duration-300',
                          isActive ? 'bg-brand-500' : 'bg-transparent'
                        )}
                      />
                      <a
                        href={`#${s.id}`}
                        onClick={(e) => handleJumpClick(e, s.id)}
                        aria-current={isActive ? 'true' : undefined}
                        className={cn(
                          'group flex items-start gap-3 px-4 py-3 text-sm transition-colors duration-200 focus:outline-none focus-visible:bg-brand-50 focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-inset rounded-sm',
                          isActive
                            ? 'text-ink-900'
                            : 'text-ink-600 hover:text-ink-900'
                        )}
                      >
                        <span
                          aria-hidden="true"
                          className={cn(
                            'font-display text-[10px] font-bold uppercase tracking-[0.15em] tabular-nums pt-0.5 shrink-0 transition-colors duration-200',
                            isActive ? 'text-brand-600' : 'text-ink-400 group-hover:text-brand-600'
                          )}
                        >
                          {String(i + 1).padStart(2, '0')}
                        </span>
                        <span
                          className={cn(
                            'leading-snug transition-[font-weight,color] duration-200',
                            isActive ? 'font-bold' : 'font-semibold'
                          )}
                        >
                          {s.title}
                        </span>
                      </a>
                    </li>
                  )
                })}
              </ol>

              {/* Footer bracket */}
              <div
                aria-hidden="true"
                className="mt-4 flex items-center gap-2"
              >
                <span className="block h-[2px] w-6 bg-brand-500" />
                <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-ink-400 tabular-nums">
                  {String(services.length).padStart(2, '0')} / {String(services.length).padStart(2, '0')}
                </span>
              </div>
            </div>
          </aside>

          {/* ---------- Service sections column ---------- */}
          <div className="lg:col-span-9 space-y-20 md:space-y-24">
            {services.map((s, i) => {
              const Icon = s.icon
              const isEven = i % 2 === 0
              const paragraphs = Array.isArray(s.paragraphs) ? s.paragraphs : []

              return (
                <section
                  key={s.key}
                  id={s.id}
                  ref={(el) => {
                    sectionRefs.current[s.id] = el
                  }}
                  aria-labelledby={`service-${s.id}-title`}
                  className="scroll-mt-28"
                >
                  {/* Section marker rail */}
                  <div className="mb-6 flex items-center gap-3">
                    <span
                      aria-hidden="true"
                      className="block h-[3px] w-14 bg-brand-500"
                    />
                    <span className="font-display text-[10px] font-bold uppercase tracking-[0.28em] text-ink-500 tabular-nums">
                      {`SVC / ${String(i + 1).padStart(2, '0')} — ${String(services.length).padStart(2, '0')}`}
                    </span>
                  </div>

                  {/* Header row: icon + title */}
                  <div className="mb-8 md:mb-10 flex items-start gap-5">
                    <div
                      aria-hidden="true"
                      className="shrink-0 flex h-14 w-14 md:h-16 md:w-16 items-center justify-center rounded-sm border border-brand-200 bg-brand-50 text-brand-600"
                    >
                      <Icon className="h-6 w-6 md:h-7 md:w-7" strokeWidth={2} />
                    </div>
                    <div className="min-w-0">
                      <p className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.22em] text-brand-600">
                        {t('services.eyebrow')}
                        {' · '}
                        <span className="text-ink-500">
                          {String(i + 1).padStart(2, '0')}
                        </span>
                      </p>
                      <h2
                        id={`service-${s.id}-title`}
                        className="fx-text-balance font-bold leading-[1.15] text-ink-900 text-2xl sm:text-3xl md:text-[2rem]"
                      >
                        {s.title}
                      </h2>
                      <p className="mt-3 max-w-2xl fx-text-pretty text-sm md:text-base text-ink-600 leading-relaxed">
                        {s.short}
                      </p>
                    </div>
                  </div>

                  {/* Content grid: image + copy block, alternating orientation */}
                  <div
                    className={cn(
                      'grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 items-start',
                      !isEven && 'md:[&>*:first-child]:order-2'
                    )}
                  >
                    {/* Image tile */}
                    <figure className="md:col-span-5">
                      <div className="relative">
                        {/* Brand corner tick marks */}
                        <span
                          aria-hidden="true"
                          className="absolute -top-2 -left-2 z-10 block h-10 w-10 border-t-[3px] border-l-[3px] border-brand-500"
                        />
                        <span
                          aria-hidden="true"
                          className="absolute -bottom-2 -right-2 z-10 block h-10 w-10 border-b-[3px] border-r-[3px] border-brand-500"
                        />
                        <div className="relative overflow-hidden rounded-sm border border-ink-200 bg-ink-200 shadow-sm">
                          <img
                            src={s.image}
                            alt={s.alt}
                            loading="lazy"
                            className="block h-full w-full object-cover aspect-[4/3]"
                          />
                          {/* Subtle brand tint to harmonize varied photography */}
                          <div
                            aria-hidden="true"
                            className="absolute inset-0 bg-gradient-to-tr from-ink-900/10 via-transparent to-brand-500/5 mix-blend-multiply"
                          />
                          {/* Floating index badge */}
                          <div className="absolute top-3 left-3 inline-flex items-center gap-2 rounded-sm bg-white/95 px-2.5 py-1 shadow-sm backdrop-blur">
                            <span
                              aria-hidden="true"
                              className="block h-1.5 w-1.5 rounded-full bg-brand-500"
                            />
                            <span className="font-display text-[10px] font-bold uppercase tracking-[0.22em] text-ink-700 tabular-nums">
                              {`Fenix / ${String(i + 1).padStart(2, '0')}`}
                            </span>
                          </div>
                        </div>
                      </div>
                    </figure>

                    {/* Copy + CTA column */}
                    <div className="md:col-span-7">
                      <div className="space-y-4 text-[15px] md:text-base leading-relaxed text-ink-700 fx-text-pretty">
                        {paragraphs.map((para, pi) => (
                          <p key={pi}>{para}</p>
                        ))}
                      </div>

                      {/* CTA panel */}
                      <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-5 pt-6 border-t border-ink-200">
                        <p className="flex-1 text-sm text-ink-600 fx-text-pretty max-w-md">
                          {t('services.sectionCtaBody')}
                        </p>
                        <LocalizedLink
                          to="contacts"
                          className="group relative inline-flex items-center gap-3 rounded-sm bg-brand-500 px-6 py-3 text-sm font-bold uppercase tracking-[0.08em] text-white shadow-[0_10px_30px_-14px_rgba(74,155,220,0.55)] transition-[background-color,transform,box-shadow] duration-200 hover:bg-brand-400 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 whitespace-nowrap"
                        >
                          <span
                            aria-hidden="true"
                            className="absolute -left-[3px] top-1/2 block h-5 w-[3px] -translate-y-1/2 bg-white/90 transition-all duration-200 group-hover:h-7"
                          />
                          {t('services.sectionCtaLabel')}
                          <ArrowRight
                            className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
                            strokeWidth={2.75}
                          />
                        </LocalizedLink>
                      </div>
                    </div>
                  </div>
                </section>
              )
            })}
          </div>
        </div>
      </div>
    </main>
  )
}
